import { ITEM_ROUTE_PATHS } from '@coursera-web/ondemand-common';
import { SkillAttainmentProgress_AttainmentStatus as SkillAttainmentProgressAttainmentStatus } from '__generated__/graphql-types';

import VerifiedSkillsPilotExperiments from 'bundles/epic/clients/__generated__/verifiedSkillsPilot';
import type { GetVerifiedSkillCourseCertificateByProductQuery } from 'bundles/skills-common/shared/verified-skills/data/certificate/__generated__/VerifiedSkillCourseCertificateByProductQuery';
import type {
  SkillAttainmentSkillAreaAttainmentFragment,
  SkillAttainmentSkillExpressionAttainmentFragment,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import { type SkillAttainmentScores } from 'bundles/skills-common/shared/verified-skills/types/types';

export const VERIFIED_SKILLS_ASSESSMENT_FEATURE = 'VERIFIED_SKILLS_ASSESSMENT';

/**
 * Returns the list of organizations included in the verified skills pilot
 * @returns {string[]} organizationIds - Array of pilot organization IDs
 */
export const getVerifiedSkillsPilotOrgIds = () => {
  const orgIds: string[] = VerifiedSkillsPilotExperiments.preview('orgIds') ?? [];

  return orgIds;
};

/**
 * Calculates skill attainment scores from skill expressions attainment data
 * @param skillExpressionsAttainment Array of skill expression attainment data
 * @returns {SkillAttainmentScores} object with calculated metrics
 */
export const calculateSkillAttainmentScores = (
  skillExpressionsAttainment: SkillAttainmentSkillExpressionAttainmentFragment[]
): SkillAttainmentScores => {
  const totalScore = skillExpressionsAttainment.length;
  const attainedScore = skillExpressionsAttainment.filter(
    (expression) => expression.attainmentStatus === SkillAttainmentProgressAttainmentStatus.FullAttainment
  ).length;

  return {
    totalScore,
    attainedScore,
  };
};

/**
 * Calculates skill attainment scores for a skill area
 * @param skillAreaAttainment Skill area attainment data
 * @returns {SkillAttainmentScores} object with calculated metrics
 */
export const calculateSkillAreaAttainmentScores = (
  skillAreaAttainment?: SkillAttainmentSkillAreaAttainmentFragment | null
): SkillAttainmentScores => {
  return calculateSkillAttainmentScores(skillAreaAttainment?.skillExpressionsAttainment || []);
};

/**
 * Calculates skill attainment for the current course
 * @param skillAreaAttainments Skill pathway attainment data
 * @param courseId
 * @returns {SkillAttainmentSkillAreaAttainmentFragment} object with calculated metrics
 */
export const getSkillAttainmentForCurrentCourse = (
  skillAreaAttainments: SkillAttainmentSkillAreaAttainmentFragment[],
  courseId: string
): SkillAttainmentSkillAreaAttainmentFragment | undefined => {
  if (!skillAreaAttainments?.length || !courseId) return undefined;

  return skillAreaAttainments.find(
    (attainment: SkillAttainmentSkillAreaAttainmentFragment) => attainment?.skillArea?.course?.id === courseId
  );
};

/**
 * Calculates attained and unattained skill attainment from skill expressions attainment data
 * @param skillAreaAttainment Skill area attainment data
 * @returns {Object} expressions - object with calculated metrics
 * @returns {SkillAttainmentSkillExpressionAttainmentFragment[]} expressions.attainedExpressions - attained expressions
 * @returns {SkillAttainmentSkillExpressionAttainmentFragment[]} expressions.unattainedExpressions - unattained expressions
 */
export const calculateAttainedAndUnattainedSkillExpressions = (
  skillAreaAttainment?: SkillAttainmentSkillAreaAttainmentFragment | null
): {
  attainedExpressions: SkillAttainmentSkillExpressionAttainmentFragment[];
  unattainedExpressions: SkillAttainmentSkillExpressionAttainmentFragment[];
} => {
  const attainedExpressions =
    skillAreaAttainment?.skillExpressionsAttainment?.filter(
      (expression) => expression.attainmentStatus === SkillAttainmentProgressAttainmentStatus.FullAttainment
    ) || [];
  const unattainedExpressions =
    skillAreaAttainment?.skillExpressionsAttainment?.filter(
      (expression) => expression.attainmentStatus === SkillAttainmentProgressAttainmentStatus.NoAttainment
    ) || [];

  return {
    attainedExpressions,
    unattainedExpressions,
  };
};

/**
 * Calculates attained and unattained skill attainment string from skill expressions attainment data for coach context
 * @param skillAreaAttainment Skill area attainment data
 * @returns {Object} expressions - object with calculated metrics
 * @returns {string} expressions.attainedExpressionsString - attained expressions string
 * @returns {string} expressions.unattainedExpressionsString - unattained expressions string
 */
export const calculateAttainedAndUnattainedSkillExpressionsStringForCoach = (
  skillAreaAttainment?: SkillAttainmentSkillAreaAttainmentFragment | null
): {
  attainedExpressionsString: string;
  unattainedExpressionsString: string;
} => {
  const { attainedExpressions, unattainedExpressions } =
    calculateAttainedAndUnattainedSkillExpressions(skillAreaAttainment);

  const attainedExpressionsString = attainedExpressions.map((expression) => expression.skillExpression.name).join(', ');
  const unattainedExpressionsString = unattainedExpressions
    .map((expression) => expression.skillExpression.name)
    .join(', ');

  return {
    attainedExpressionsString,
    unattainedExpressionsString,
  };
};

/** Union from `Certificate.getMyCertificateByProduct` for the verified-skills certificate query. */
export type VerifiedSkillCourseCertificateByProductData = NonNullable<
  NonNullable<GetVerifiedSkillCourseCertificateByProductQuery['Certificate']>['getMyCertificateByProduct']
>;

/**
 * True when the learner has completed the course: issued course certificate, or course validation passed.
 * Used by verified-skills result flows; does not cover specialization-only validation shapes.
 */
export const isVerifiedSkillCourseCompleted = (
  certificate: VerifiedSkillCourseCertificateByProductData | undefined
): boolean => {
  if (!certificate) {
    return false;
  }

  if (certificate.__typename === 'Certificates_Certificate' && certificate.product?.__typename === 'Course_Course') {
    return true;
  }

  if (certificate.__typename === 'Certificates_CourseCertificateValidation') {
    return certificate.isPassed;
  }

  return false;
};

export const getAssessmentRoutePath = (itemTypeName?: string) => {
  if (!itemTypeName) {
    return '';
  }

  const normalizedItemTypeName = itemTypeName.replace('OnDemandLearnerMaterialItemsV1_', '').replace('Member', '');

  if (normalizedItemTypeName === 'gradedLab') {
    return ITEM_ROUTE_PATHS.GRADED_LAB;
  }

  if (normalizedItemTypeName === 'gradedCoach') {
    return ITEM_ROUTE_PATHS.GRADED_COACH;
  }

  if (normalizedItemTypeName === 'staffGraded') {
    return ITEM_ROUTE_PATHS.ASSIGNMENT_SUBMISSION;
  }

  return '';
};

export const getSkillAssessmentMappingType = (itemTypeName?: string) => {
  if (!itemTypeName) {
    return '';
  }

  const normalizedItemTypeName = itemTypeName.replace('OnDemandLearnerMaterialItemsV1_', '').replace('Member', '');

  if (normalizedItemTypeName === 'gradedLab') {
    return 'lab';
  }

  if (normalizedItemTypeName === 'gradedCoach') {
    return 'roleplay';
  }

  if (normalizedItemTypeName === 'staffGraded') {
    return 'assignment';
  }

  return '';
};

/**
 * Checks if an item is a skill assessment item. This is mainly used inside Enterprise pages as they do not have access to the computed item.
 * @param itemTypeName - The item type name
 * @param assignmentFeatures - The assignment features
 * @returns True if the item is a skill assessment item, false otherwise
 */
export const getIsSkillAssessmentItem = (itemTypeName?: string, assignmentFeatures?: string[]) => {
  const normalizedItemTypeName = itemTypeName?.replace('OnDemandLearnerMaterialItemsV1_', '').replace('Member', '');

  return (
    (normalizedItemTypeName === 'staffGraded' && assignmentFeatures?.includes(VERIFIED_SKILLS_ASSESSMENT_FEATURE)) ||
    /* All graded labs and graded role plays in a verified skills course are skill assessments */
    normalizedItemTypeName === 'gradedLab' ||
    normalizedItemTypeName === 'gradedCoach'
  );
};

type SkillPassingThresholdForAssessmentArgs = {
  skillExpressionsAttainment?: SkillAttainmentSkillExpressionAttainmentFragment[];
  passingFraction?: number;
};

/**
 * Calculates the total and passing threshold for skill assessments.
 * Returns the total number of items and the passing threshold.
 */
export const getSkillPassingThresholdForAssessment = ({
  skillExpressionsAttainment,
  passingFraction = 0,
}: SkillPassingThresholdForAssessmentArgs) => {
  // Ensure passing fraction is between 0 and 1 (0% - 100%)
  const normalizedPassingFraction = Math.max(0, Math.min(1, passingFraction ?? 0));

  const totalSkillExpressions = skillExpressionsAttainment?.length || 0;
  const totalSkillsAttained =
    skillExpressionsAttainment?.filter(
      (skillExpressionAttainment) =>
        skillExpressionAttainment.attainmentStatus === SkillAttainmentProgressAttainmentStatus.FullAttainment
    ).length || 0;
  const skillAttainmentThreshold = Math.ceil(totalSkillExpressions * normalizedPassingFraction);

  return {
    isPassed: totalSkillsAttained > 0 && totalSkillsAttained >= skillAttainmentThreshold,
    skillAttainmentThreshold,
    totalSkillsAttained,
    totalSkillsAttainable: totalSkillExpressions,
  };
};
